import {
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"

import { ReactComponent as SvgCross } from "@common_assets/svg/cross.svg"
import { ReactComponent as SvgResearch } from "@common_assets/svg/research.svg"
import { Box, Button, IconButton, Stack, Typography } from "@mui/material"
import { useQueryAdminDashboardStudents } from "api/reactQuery/queries/adminDashboard"
import NoElementsMessage from "components/common/listing/noElementsMessage"
import NoMatchesForSearch from "components/common/listing/noMatchesForSearch"
import Pagination from "components/common/pagination"
import SearchbarForm from "components/form/searchbarForm"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import {
  IDashboardPeriodParams,
  IDashboardStudentRow,
  IDashboardStudentsParams,
} from "ts/interfaces/AdminDashboard"
import {
  DashboardStudentAttentionView,
  DashboardStudentSortKey,
} from "ts/types/AdminDashboard"

import ClassFilter from "./ClassFilter"
import { Amount, ClassCell, Dash } from "./DashboardCells"
import DashboardSection from "./DashboardSection"
import DashboardSectionBody, { useShownValue } from "./DashboardSectionBody"
import DashboardTable, { IDashboardTableColumn } from "./DashboardTable"
import PositivityChip from "./PositivityChip"
import useDashboardTablePaging, {
  DASHBOARD_PAGE_SIZE,
} from "./useDashboardTablePaging"
import useStudentsTableState from "./useStudentsTableState"

const formatName = (row: IDashboardStudentRow) =>
  [row.first_name, row.last_name].filter(Boolean).join(" ") || "—"

// The shortlist each column-less sort comes from, named in the "Sorted by" chip.
const SORTED_BY_VIEW: Partial<
  Record<DashboardStudentSortKey, DashboardStudentAttentionView>
> = {
  fine_count: "most_fined",
  bonus_count: "fewest_bonuses_earned",
}

export interface IAllStudentsHandle {
  applyView: (view: DashboardStudentAttentionView) => void
}

const AllStudents = forwardRef<
  IAllStudentsHandle,
  { params: IDashboardPeriodParams }
>(({ params }, ref) => {
  const { t } = useTranslation()
  const {
    search,
    classIds,
    sortBy,
    sortDir,
    page,
    setSearch,
    setClassIds,
    clearFilters,
    applyView,
    clearSort,
    toggleSort,
    nameSortKey,
    nameSortSuffix,
    setPage,
  } = useStudentsTableState()
  // BED returns 422 for an empty `search`, so the key is left out instead.
  const table: IDashboardStudentsParams = {
    ...(search && { search }),
    ...(classIds.length > 0 && { classes: classIds }),
    sort_by: sortBy,
    sorting: sortDir,
    skip: (page - 1) * DASHBOARD_PAGE_SIZE,
    limit: DASHBOARD_PAGE_SIZE,
  }
  const query = useQueryAdminDashboardStudents(params, table)
  const shownPage = useShownValue(page, query.isPreviousData)
  const shownSearch = useShownValue(search, query.isPreviousData)
  const hasShownClasses =
    useShownValue(classIds.join(","), query.isPreviousData) !== ""
  const data = query.data?.data
  const rows = data?.objects ?? []
  const total = data?.all_objects_count ?? null
  const isAttendanceOff = data?.has_attendance_integration === false
  const from = (shownPage - 1) * DASHBOARD_PAGE_SIZE + 1
  const to = from + rows.length - 1
  // A stale link's page past the end isn't empty; it's clamped below.
  const isEmpty =
    !!data && rows.length === 0 && (total === 0 || shownPage === 1)
  const summary =
    total === null
      ? undefined
      : t("adminDashboard.showingStudents", {
          from,
          to,
          total: total.toLocaleString("en-US"),
        })
  let emptyText = t("adminDashboard.noStudentsYet")
  if (shownSearch) emptyText = t("setup.noMatchesFor", { match: shownSearch })
  if (hasShownClasses)
    emptyText = shownSearch
      ? t("adminDashboard.noMatchesInClasses", { match: shownSearch })
      : t("adminDashboard.noStudentsInClasses")

  const { pageCount, announcement, announceNextResult } =
    useDashboardTablePaging({
      query,
      page,
      shownPage,
      rowCount: rows.length,
      total,
      setPage,
      resultText: isEmpty ? emptyText : (summary ?? ""),
    })

  const regionRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  // A new key remounts the search field, emptying it and dropping a pending
  // debounce, when the search is cleared from outside the field.
  const [searchKey, setSearchKey] = useState(0)
  const isSearchFocusPending = useRef(false)

  useEffect(() => {
    if (!isSearchFocusPending.current) return
    isSearchFocusPending.current = false
    searchRef.current?.focus()
  }, [searchKey])

  const handleSearch = (value: string) => {
    announceNextResult()
    setSearch(value)
  }

  const handleClassIds = (ids: number[]) => {
    announceNextResult()
    setClassIds(ids)
  }

  const handleClearFilters = () => {
    announceNextResult()
    clearFilters()
    isSearchFocusPending.current = true
    setSearchKey((key) => key + 1)
  }

  // Brings the admin to the table, now in the shortlist's order.
  const handleApplyView = (view: DashboardStudentAttentionView) => {
    announceNextResult()
    applyView(view)
    setSearchKey((key) => key + 1)
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
    titleRef.current?.scrollIntoView({
      block: "start",
      behavior: reduceMotion ? "auto" : "smooth",
    })
    titleRef.current?.focus({ preventScroll: true })
  }

  useImperativeHandle(ref, () => ({ applyView: handleApplyView }))

  // The chip goes away with its ×, so focus moves to the section instead.
  const handleClearSort = () => {
    announceNextResult()
    clearSort()
    titleRef.current?.focus()
  }

  const sortedByView = SORTED_BY_VIEW[sortBy]

  const handleSort = (key: DashboardStudentSortKey) => {
    announceNextResult()
    toggleSort(key)
  }

  const handlePageChange = (value: number) => {
    announceNextResult()
    setPage(value)
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
    regionRef.current?.scrollIntoView({
      block: "start",
      behavior: reduceMotion ? "auto" : "smooth",
    })
    regionRef.current?.focus({ preventScroll: true })
  }

  let emptyContent: ReactNode = emptyText
  if (shownSearch) emptyContent = <NoMatchesForSearch search={shownSearch} />
  if (hasShownClasses)
    emptyContent = (
      <Box py={{ mobile: "16px", desktop: "64px" }}>
        <NoElementsMessage
          title={emptyText}
          Icon={<SvgResearch />}
          bottomSlot={
            <Button variant="text" onClick={handleClearFilters}>
              {t("adminDashboard.clearFilters")}
            </Button>
          }
        />
      </Box>
    )

  const amount = (value: number) => <Amount value={value} />

  const columns: IDashboardTableColumn<
    IDashboardStudentRow,
    DashboardStudentSortKey
  >[] = [
    {
      id: "student",
      label: `${t("adminDashboard.column_student")}${nameSortSuffix}`,
      sortKey: nameSortKey,
      render: formatName,
    },
    {
      id: "class",
      label: t("adminDashboard.classColumn"),
      render: (row) => <ClassCell classes={row.classes} />,
    },
    {
      id: "positivity",
      label: t("adminDashboard.column_positivity"),
      sortKey: "positivity",
      render: (row) => <PositivityChip positivity={row.positivity} />,
    },
    {
      id: "total",
      label: t("adminDashboard.column_total"),
      sortKey: "total_amount",
      align: "right",
      render: (row) => amount(row.total_amount),
    },
    {
      id: "bonus",
      label: t("adminDashboard.column_bonus"),
      sortKey: "bonus_amount",
      align: "right",
      render: (row) => amount(row.bonus_amount),
    },
    {
      id: "fine",
      label: t("adminDashboard.column_fine"),
      sortKey: "fine_amount",
      align: "right",
      render: (row) => amount(row.fine_amount),
    },
    {
      id: "attendance",
      label: t("adminDashboard.column_attendance"),
      sortKey: "attendance_count",
      align: "right",
      disabledReason: isAttendanceOff
        ? t("adminDashboard.attendanceDaysNotConnected")
        : undefined,
      render: (row) => {
        if (isAttendanceOff)
          return <Dash label={t("adminDashboard.attendanceNotConnectedCell")} />
        if (row.attendance_count === null)
          return <Dash label={t("adminDashboard.attendanceNoData")} />
        return (
          <>
            <span aria-hidden="true">
              {row.attendance_count.toLocaleString("en-US")}
            </span>
            <Box component="span" sx={visuallyHiddenSx}>
              {t("adminDashboard.daysAttended", {
                count: row.attendance_count,
              })}
            </Box>
          </>
        )
      },
    },
    {
      id: "salary",
      label: t("adminDashboard.column_salary"),
      sortKey: "salary_amount",
      align: "right",
      render: (row) => amount(row.salary_amount),
    },
    {
      id: "expense",
      label: t("adminDashboard.column_expense"),
      sortKey: "expense_amount",
      align: "right",
      render: (row) => amount(row.expense_amount),
    },
  ]

  const tableProps = {
    label: t("adminDashboard.allStudentsTable"),
    columns,
    getRowId: (row: IDashboardStudentRow) => row.fk_student_id,
    sortBy,
    sortDir,
    onSort: handleSort,
  }

  return (
    <DashboardSection
      title={t("adminDashboard.allStudents")}
      question={t("adminDashboard.allStudentsQuestion")}
      questionGap={1}
      titleRef={titleRef}
    >
      <Stack rowGap={2}>
        {/* With the stack's gap, 32px above and below the toolbar */}
        <Stack
          direction={{ mobile: "column", tablet: "row" }}
          gap={2}
          sx={{ py: 2 }}
        >
          <SearchbarForm
            key={searchKey}
            inputRef={searchRef}
            initialValue={search}
            clearable
            placeholder={t("adminDashboard.searchStudent")}
            onSubmit={(form) => handleSearch(form.search)}
          />
          <ClassFilter
            classIds={classIds}
            onChange={handleClassIds}
            onStaleIds={setClassIds}
          />
        </Stack>
        {sortedByView && (
          <Stack
            direction="row"
            alignItems="center"
            alignSelf="flex-start"
            gap={0.5}
            sx={{
              height: 32,
              pl: 1.5,
              pr: 0.5,
              borderRadius: "16px",
              bgcolor: "mockup.primary95",
              color: "mockup.primary60",
            }}
          >
            <Typography variant="body2" fontWeight={600} color="inherit">
              {t("adminDashboard.sortedBy", {
                view: t(`adminDashboard.attentionView_${sortedByView}`),
              })}
            </Typography>
            <IconButton
              aria-label={t("adminDashboard.clearSort")}
              onClick={handleClearSort}
              sx={{ color: "inherit", width: 24, height: 24, p: 0 }}
            >
              <Box
                component={SvgCross}
                aria-hidden
                sx={{ width: 20, height: 20 }}
              />
            </IconButton>
          </Stack>
        )}
        <DashboardSectionBody
          queries={[query]}
          // Centred in the 32px gap, clear of the search field
          progressTop={-17}
          isEmpty={isEmpty}
          emptyText={emptyContent}
          skeleton={
            <DashboardTable
              {...tableProps}
              rows={[]}
              skeletonRows={DASHBOARD_PAGE_SIZE}
            />
          }
        >
          <DashboardTable {...tableProps} rows={rows} regionRef={regionRef} />
        </DashboardSectionBody>
        {rows.length > 0 && (
          <Pagination
            page={page}
            count={pageCount}
            onChange={handlePageChange}
            summary={summary}
          />
        )}
      </Stack>
      <Box role="status" sx={visuallyHiddenSx}>
        {announcement}
      </Box>
    </DashboardSection>
  )
})

AllStudents.displayName = "AllStudents"

export default AllStudents
